import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { StockPolicyListRow } from "@/modules/inventory/domain/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatStockPolicyApiError } from "../../api/stock-policies-client";
import {
  usePatchStockPolicyMutation,
  useUpsertStockPolicyMutation,
} from "../../api/stock-policies-queries";
import type { PoCatalogPick } from "../purchase/poCatalogTypes";
import {
  stockPolicyItemTypeLabel,
  stockPolicyScopeLabel,
} from "./stockPolicyLaoLabels";

type StockPolicyEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** แก้ไขแถวที่มีอยู่ */
  row?: StockPolicyListRow | null;
  /** เพิ่มจาก catalog */
  pick?: PoCatalogPick | null;
  policyBranchId: string | null;
  defaultMinQty?: number;
  defaultTargetQty?: number;
};

export function StockPolicyEditDialog({
  open,
  onOpenChange,
  row,
  pick,
  policyBranchId,
  defaultMinQty = 10,
  defaultTargetQty,
}: StockPolicyEditDialogProps) {
  const upsert = useUpsertStockPolicyMutation();
  const patch = usePatchStockPolicyMutation();

  const [minQty, setMinQty] = useState(String(defaultMinQty));
  const [targetQty, setTargetQty] = useState(
    defaultTargetQty != null ? String(defaultTargetQty) : "",
  );
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    if (row) {
      setMinQty(String(row.minQty));
      setTargetQty(row.targetQty != null ? String(row.targetQty) : "");
      setNote(row.note ?? "");
    } else {
      setMinQty(String(defaultMinQty));
      setTargetQty(
        defaultTargetQty != null ? String(defaultTargetQty) : String(defaultMinQty),
      );
      setNote("");
    }
  }, [open, row, defaultMinQty, defaultTargetQty]);

  const title = row
    ? "ແກ້ເງື່ອນໄຂສຕັອກ"
    : pick
      ? "ຕັ້ງເງື່ອນໄຂສຕັອກ"
      : "ເງື່ອນໄຂສຕັອກ";

  const itemLabel = row?.itemName ?? pick?.label ?? "";

  const onSave = async () => {
    const min = Number.parseFloat(minQty);
    const targetRaw = targetQty.trim();
    const target = targetRaw ? Number.parseFloat(targetRaw) : null;
    if (!Number.isFinite(min) || min <= 0) {
      toast.error("ຂັ້ນຕ່ຳຕ້ອງໃຫຍ່ກວ່າ 0");
      return;
    }
    if (target != null && (!Number.isFinite(target) || target < min)) {
      toast.error("ເປົ້າເຕີມຕ້ອງ ≥ ຂັ້ນຕ່ຳ");
      return;
    }

    try {
      if (row) {
        await patch.mutateAsync({
          id: row.id,
          body: {
            minQty: min,
            targetQty: target,
            note: note.trim() || null,
          },
        });
      } else if (pick) {
        await upsert.mutateAsync({
          itemType: pick.itemType,
          itemId: pick.itemId,
          branchId: policyBranchId,
          minQty: min,
          targetQty: target,
          note: note.trim() || null,
        });
      }
      toast.success("ບັນທຶກເກັນໄຂສຳເລັດ");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatStockPolicyApiError(e));
    }
  };

  const pending = upsert.isPending || patch.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {itemLabel ? (
            <p>
              <span className="text-muted-foreground">ລາຍການ: </span>
              <span className="font-medium">{itemLabel}</span>
              {pick ? (
                <span className="text-muted-foreground">
                  {" "}
                  · {stockPolicyItemTypeLabel(pick.itemType)}
                </span>
              ) : row ? (
                <span className="text-muted-foreground">
                  {" "}
                  · {stockPolicyItemTypeLabel(row.itemType)}
                </span>
              ) : null}
            </p>
          ) : null}
          <p className="text-muted-foreground">
            {stockPolicyScopeLabel(
              policyBranchId,
              row?.branchCode ?? null,
            )}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="font-medium text-sm" htmlFor="sp-min">
                ຂັ້ນຕ່ຳ (min) *
              </label>
              <Input
                id="sp-min"
                type="number"
                min={0}
                step="any"
                value={minQty}
                onChange={(e) => setMinQty(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-medium text-sm" htmlFor="sp-target">
                ເປົ້າເຕີມ (target)
              </label>
              <Input
                id="sp-target"
                type="number"
                min={0}
                step="any"
                placeholder="ຄື min ຖ້າວ່າງ"
                value={targetQty}
                onChange={(e) => setTargetQty(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="font-medium text-sm" htmlFor="sp-note">
              ໝາຍເຫດ
            </label>
            <Input
              id="sp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ທາງເລືອກ"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
          <Button onClick={() => void onSave()} isLoading={pending}>
            ບັນທຶກ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
